import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import PublicLayout from '../components/PublicLayout';
import {
  Sparkles, Image as ImageIcon, ListChecks, Target, Calendar as CalendarIcon,
  BookOpen, Trophy, DollarSign, Brain, Crown, Check, ArrowRight, Shield,
  Heart, Lock, Infinity as InfinityIcon,
} from 'lucide-react';
import api from '../lib/api';

const FEATURE_DEFS = [
  { icon: ImageIcon,    titleKey: 'feat_vb_t',      descKey: 'feat_vb_d' },
  { icon: ListChecks,   titleKey: 'feat_habits_t',  descKey: 'feat_habits_d' },
  { icon: Target,       titleKey: 'feat_goals_t',   descKey: 'feat_goals_d' },
  { icon: CalendarIcon, titleKey: 'feat_cal_t',     descKey: 'feat_cal_d' },
  { icon: BookOpen,     titleKey: 'feat_journal_t', descKey: 'feat_journal_d' },
  { icon: Brain,        titleKey: 'feat_clarity_t', descKey: 'feat_clarity_d' },
  { icon: Trophy,       titleKey: 'feat_achv_t',    descKey: 'feat_achv_d' },
  { icon: DollarSign,   titleKey: 'feat_budget_t',  descKey: 'feat_budget_d' },
];

const BENEFIT_DEFS = [
  { icon: Heart,  titleKey: 'bnf_rhythm_t', descKey: 'bnf_rhythm_d' },
  { icon: Shield, titleKey: 'bnf_data_t',   descKey: 'bnf_data_d' },
  { icon: Lock,   titleKey: 'bnf_pay_t',    descKey: 'bnf_pay_d' },
];

const PLAN_TAGLINE_KEYS = {
  free: 'tag_free',
  light: 'tag_light',
  pro: 'tag_pro',
  vip: 'tag_vip',
  trimestral: 'tag_trimestral',
};

const Landing = () => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const { t } = useLanguage();
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    let cancelled = false;
    api.get('/dlocal/plans')
      .then((r) => { if (!cancelled) setPlans(r.data?.plans || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const PLAN_ORDER = ['free', 'light', 'pro', 'vip', 'trimestral'];
  const orderedPlans = [...plans].sort(
    (a, b) => PLAN_ORDER.indexOf(a.id) - PLAN_ORDER.indexOf(b.id)
  );

  const ctaHref = user ? '/dashboard' : '/login?mode=register';

  return (
    <PublicLayout>
      {/* ==== HERO ==== */}
      <section className="relative px-4 sm:px-6 lg:px-8 pt-16 pb-20 sm:pt-24 sm:pb-28">
        <div className="max-w-5xl mx-auto text-center">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] uppercase tracking-widest mb-6"
            style={{ background: `${theme.accent}15`, color: theme.accent, border: `1px solid ${theme.accent}30` }}>
            <Sparkles size={12} /> {t('landing_badge')}
          </span>
          <h1 className="text-4xl sm:text-6xl lg:text-7xl font-light tracking-tight mb-6"
            style={{ color: theme.textPrimary }}>
            {t('landing_hero_title_1')}{' '}
            <span style={{ color: theme.accent }}>{t('landing_hero_title_2')}</span>.
          </h1>
          <p className="text-base sm:text-lg max-w-2xl mx-auto leading-relaxed mb-10"
            style={{ color: theme.textSecondary }}>
            {t('landing_hero_subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
            <Link to={ctaHref} data-testid="hero-cta-primary"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-medium text-sm transition-all hover:scale-105"
              style={{ background: theme.accent, color: theme.bg, boxShadow: `0 12px 32px ${theme.accent}50` }}>
              {t('landing_cta_primary')} <ArrowRight size={14} />
            </Link>
            <Link to="/#planes" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full text-sm"
              style={{ color: theme.textSecondary, border: `1px solid ${theme.border}` }}>
              {t('landing_cta_secondary')}
            </Link>
          </div>
          <p className="text-[11px] mt-4" style={{ color: theme.textMuted }}>
            {t('landing_hero_disclaimer')}
          </p>
        </div>
      </section>

      {/* ==== FEATURES ==== */}
      <section id="funciones" className="px-4 sm:px-6 lg:px-8 py-16">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-[11px] uppercase tracking-[0.25em] mb-2" style={{ color: theme.textMuted }}>
              {t('landing_features_label')}
            </p>
            <h2 className="text-3xl sm:text-4xl font-light tracking-tight" style={{ color: theme.textPrimary }}>
              {t('landing_features_title')}
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {FEATURE_DEFS.map((f) => {
              const Icon = f.icon;
              return (
                <div key={f.titleKey} className="rounded-2xl p-5 transition-all hover:scale-[1.02]"
                  style={{ background: theme.card, border: `1px solid ${theme.border}` }}>
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3"
                    style={{ background: `${theme.accent}20`, border: `1px solid ${theme.accent}40` }}>
                    <Icon size={18} style={{ color: theme.accent }} />
                  </div>
                  <h3 className="text-base font-medium mb-1.5" style={{ color: theme.textPrimary }}>{t(f.titleKey)}</h3>
                  <p className="text-[12.5px] leading-snug" style={{ color: theme.textSecondary }}>{t(f.descKey)}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ==== BENEFITS ==== */}
      <section className="px-4 sm:px-6 lg:px-8 py-16">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-[11px] uppercase tracking-[0.25em] mb-2" style={{ color: theme.textMuted }}>
              {t('landing_benefits_label')}
            </p>
            <h2 className="text-3xl sm:text-4xl font-light tracking-tight" style={{ color: theme.textPrimary }}>
              {t('landing_benefits_title')}
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            {BENEFIT_DEFS.map((b) => {
              const Icon = b.icon;
              return (
                <div key={b.titleKey} className="text-center">
                  <div className="w-12 h-12 rounded-2xl flex items-center justify-center mx-auto mb-3"
                    style={{ background: `${theme.primary}25`, border: `1px solid ${theme.primary}50` }}>
                    <Icon size={20} style={{ color: theme.accent }} />
                  </div>
                  <h3 className="text-sm font-medium mb-1.5" style={{ color: theme.textPrimary }}>{t(b.titleKey)}</h3>
                  <p className="text-[12px] leading-snug" style={{ color: theme.textSecondary }}>{t(b.descKey)}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ==== PLANS ==== */}
      <section id="planes" className="px-4 sm:px-6 lg:px-8 py-16 scroll-mt-20">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-[11px] uppercase tracking-[0.25em] mb-2" style={{ color: theme.textMuted }}>
              {t('landing_plans_label')}
            </p>
            <h2 className="text-3xl sm:text-4xl font-light tracking-tight mb-3" style={{ color: theme.textPrimary }}>
              {t('landing_plans_title')}
            </h2>
            <p className="text-sm max-w-2xl mx-auto" style={{ color: theme.textSecondary }}>
              {t('landing_plans_subtitle_pre')} <strong style={{ color: theme.accent }}>Premium</strong> {t('landing_plans_subtitle_post')}
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            {orderedPlans.map((plan) => {
              const isPro = plan.id === 'vip';
              const isFree = plan.id === 'free';
              const period = plan.frequency_value > 1 ? `${plan.frequency_value} ${t('plan_period_months')}` : t('plan_period_month');
              const taglineKey = PLAN_TAGLINE_KEYS[plan.id];
              return (
                <div key={plan.id} className="relative rounded-2xl p-5 flex flex-col"
                  style={{
                    background: isPro
                      ? `linear-gradient(165deg, ${theme.primary}35, ${theme.card} 55%)`
                      : theme.card,
                    border: `1px solid ${isPro ? theme.accent : theme.border}`,
                    boxShadow: isPro ? `0 16px 40px ${theme.primary}30` : 'none',
                    minHeight: 360,
                  }}
                  data-testid={`landing-plan-${plan.id}`}>
                  {isPro && (
                    <span className="absolute -top-2.5 right-4 px-2.5 py-0.5 rounded-full text-[9px] uppercase tracking-widest font-bold flex items-center gap-1"
                      style={{ background: theme.accent, color: theme.bg }}>
                      <Crown size={10} /> {t('plan_most_popular')}
                    </span>
                  )}
                  <div className="mb-4">
                    <div className="flex items-baseline justify-between">
                      <h3 className="text-xl font-medium" style={{ color: theme.textPrimary }}>{plan.name}</h3>
                      {isPro && <InfinityIcon size={16} style={{ color: theme.accent }} />}
                    </div>
                    <p className="text-[11px] mt-1" style={{ color: theme.textMuted }}>
                      {taglineKey ? t(taglineKey) : ''}
                    </p>
                  </div>
                  <div className="mb-4">
                    <p className="text-4xl font-light tracking-tight" style={{ color: theme.accent }}>
                      ${plan.price}
                      <span className="text-xs ml-1.5" style={{ color: theme.textMuted }}>USD / {period}</span>
                    </p>
                  </div>
                  <ul className="space-y-2 mb-5 flex-1 text-[12px]">
                    <li className="flex items-start gap-2" style={{ color: theme.textSecondary }}>
                      <Check size={13} className="mt-0.5 shrink-0" style={{ color: theme.success }} />
                      {plan.credits_monthly === null ? t('plan_credits_unlimited') : `${plan.credits_monthly} ${t('plan_credits_count')}`}
                    </li>
                    <li className="flex items-start gap-2" style={{ color: theme.textSecondary }}>
                      <Check size={13} className="mt-0.5 shrink-0" style={{ color: theme.success }} />
                      {plan.limits?.vision_boards === null ? t('plan_boards_unlimited') : `${plan.limits?.vision_boards} ${t('plan_boards_count')}`}
                    </li>
                    <li className="flex items-start gap-2" style={{ color: theme.textSecondary }}>
                      <Check size={13} className="mt-0.5 shrink-0" style={{ color: theme.success }} />
                      {plan.limits?.habits === null ? t('plan_habits_unlimited') : `${plan.limits?.habits} ${t('plan_habits_count')}`}
                    </li>
                    <li className="flex items-start gap-2" style={{ color: plan.limits?.budget_access ? theme.accent : theme.textMuted }}>
                      {plan.limits?.budget_access
                        ? <Check size={13} className="mt-0.5 shrink-0" style={{ color: theme.accent }} />
                        : <Lock size={13} className="mt-0.5 shrink-0" />}
                      {plan.limits?.budget_access ? t('plan_budget_included') : t('plan_budget_excluded')}
                    </li>
                  </ul>
                  <Link to={ctaHref}
                    className="w-full text-center text-sm py-2.5 rounded-md font-medium"
                    style={{
                      background: isFree ? theme.elevated : (isPro ? theme.accent : theme.primary),
                      color: isFree ? theme.textPrimary : (isPro ? theme.bg : '#fff'),
                      border: isFree ? `1px solid ${theme.border}` : 'none',
                    }}>
                    {isFree ? t('plan_btn_start_free') : t('plan_btn_subscribe')}
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ==== CTA FINAL ==== */}
      <section className="px-4 sm:px-6 lg:px-8 py-20">
        <div className="max-w-4xl mx-auto rounded-3xl p-10 sm:p-14 text-center"
          style={{
            background: `linear-gradient(135deg, ${theme.primary}30, ${theme.card})`,
            border: `1px solid ${theme.accent}40`,
          }}>
          <h2 className="text-3xl sm:text-4xl font-light tracking-tight mb-4" style={{ color: theme.accent }}>
            {t('landing_cta_final_title')}
          </h2>
          <p className="text-sm max-w-xl mx-auto mb-7" style={{ color: theme.textSecondary }}>
            {t('landing_cta_final_subtitle')}
          </p>
          <Link to={ctaHref} data-testid="cta-bottom"
            className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full font-medium text-sm"
            style={{ background: theme.accent, color: theme.bg, boxShadow: `0 12px 32px ${theme.accent}50` }}>
            {t('landing_cta_final_btn')} <ArrowRight size={14} />
          </Link>
          <p className="text-[11px] mt-4" style={{ color: theme.textMuted }}>
            {t('landing_questions')}{' '}
            <a href="mailto:asistencia.teko@gmail.com" style={{ color: theme.accent }}>asistencia.teko@gmail.com</a>
          </p>
        </div>
      </section>
    </PublicLayout>
  );
};

export default Landing;
